import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { respondToAssignment } from '../../api/appointments';
import AppointmentDetailDrawer from '../appointments/AppointmentDetailDrawer';
import StaffAssignmentBadge from '../appointments/StaffAssignmentBadge';
import StatusBadge from '../appointments/StatusBadge';
import { useToast } from '../../context/ToastContext';
import { getFriendlyError } from '../../utils/friendlyError';

const formatDayLabel = (dateKey, t) => {
  const date = new Date(`${dateKey}T12:00:00`);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const tomorrow = new Date(today);
  tomorrow.setDate(today.getDate() + 1);
  const dayStart = new Date(date);
  dayStart.setHours(0, 0, 0, 0);

  if (dayStart.getTime() === today.getTime()) return t('common.today');
  if (dayStart.getTime() === tomorrow.getTime()) return t('common.tomorrow');
  return date.toLocaleDateString(undefined, { weekday: 'long', month: 'short', day: 'numeric' });
};

const formatWeekRange = (start, end) => {
  const s = new Date(start);
  const e = new Date(end);
  e.setDate(e.getDate() - 1);
  const opts = { month: 'short', day: 'numeric' };
  return `${s.toLocaleDateString(undefined, opts)} – ${e.toLocaleDateString(undefined, opts)}`;
};

const StaffWeekSchedule = ({ weekRange, summary, pendingAssignments = [], weekAppointments = [] }) => {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const queryClient = useQueryClient();
  const [selectedId, setSelectedId] = useState(null);
  const [rejectingId, setRejectingId] = useState(null);
  const [rejectReason, setRejectReason] = useState('');

  const assignmentMut = useMutation({
    mutationFn: ({ id, action, reason }) => respondToAssignment(id, { action, reason }),
    onSuccess: (res) => {
      showToast(res.message || t('staff.dashboard.assignmentUpdated'), 'success');
      setRejectingId(null);
      setRejectReason('');
      queryClient.invalidateQueries({ queryKey: ['staff-dashboard'] });
      queryClient.invalidateQueries({ queryKey: ['appointments'] });
    },
    onError: (err) => showToast(getFriendlyError(err), 'error'),
  });

  const groupedWeek = useMemo(() => {
    const groups = {};
    for (const appointment of weekAppointments) {
      if (!appointment.appointmentDate) continue;
      const key = new Date(appointment.appointmentDate).toISOString().split('T')[0];
      if (!groups[key]) groups[key] = [];
      groups[key].push(appointment);
    }
    return Object.entries(groups).sort(([a], [b]) => a.localeCompare(b));
  }, [weekAppointments]);

  const handleReject = (id) => {
    const reason = rejectReason.trim();
    if (!reason) {
      showToast(t('appointments.drawer.assignmentRejectReason'), 'error');
      return;
    }
    assignmentMut.mutate({ id, action: 'reject', reason });
  };

  const renderAppointmentRow = (appointment, showActions = false) => (
    <article
      key={appointment._id}
      className="rounded-lg border border-zinc-200 bg-zinc-50/50 p-4 dark:border-zinc-800 dark:bg-zinc-950/40"
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <button type="button" onClick={() => setSelectedId(appointment._id)} className="min-w-0 flex-1 text-left">
          <p className="font-medium">{appointment.serviceName}</p>
          <p className="mt-1 text-sm text-zinc-600 dark:text-zinc-400">
            {appointment.clientName}
            {appointment.branchName ? ` · ${appointment.branchName}` : ''}
          </p>
          <p className="mt-1 text-xs text-zinc-500">
            {appointment.appointmentTime || t('staff.dashboard.timeTbd')}
            {appointment.serviceDuration ? ` · ${t('common.duration', { minutes: appointment.serviceDuration })}` : ''}
          </p>
          {appointment.awaitingStaffSlotApproval && (
            <p className="mt-1 text-xs text-amber-500">{t('appointments.drawer.slotApprovalPrompt')}</p>
          )}
        </button>
        <div className="flex flex-col items-end gap-1">
          <StatusBadge status={appointment.status} />
          {appointment.staffAssignmentStatus && (
            <StaffAssignmentBadge status={appointment.staffAssignmentStatus} />
          )}
        </div>
      </div>

      {showActions && (
        <div className="mt-4 border-t border-zinc-200 pt-4 dark:border-zinc-800">
          {rejectingId === appointment._id ? (
            <div className="space-y-2">
              <textarea
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder={t('appointments.drawer.assignmentRejectReason')}
                rows={2}
                className="w-full rounded-lg border border-zinc-300 bg-white px-3 py-2 text-sm dark:border-zinc-700 dark:bg-zinc-900"
              />
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => { setRejectingId(null); setRejectReason(''); }}
                  className="rounded-lg border border-zinc-300 px-3 py-1.5 text-sm dark:border-zinc-700"
                >
                  {t('common.cancel')}
                </button>
                <button
                  type="button"
                  disabled={assignmentMut.isPending}
                  onClick={() => handleReject(appointment._id)}
                  className="rounded-lg bg-red-600 px-3 py-1.5 text-sm text-white disabled:opacity-50"
                >
                  {t('appointments.drawer.declineAssignment')}
                </button>
              </div>
            </div>
          ) : (
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                disabled={assignmentMut.isPending}
                onClick={() => assignmentMut.mutate({ id: appointment._id, action: 'accept' })}
                className="rounded-lg bg-emerald-600 px-3 py-2 text-sm text-white hover:bg-emerald-500 disabled:opacity-50"
              >
                {t('appointments.drawer.acceptAssignment')}
              </button>
              <button
                type="button"
                disabled={assignmentMut.isPending}
                onClick={() => { setRejectingId(appointment._id); setRejectReason(''); }}
                className="rounded-lg border border-red-300 px-3 py-2 text-sm text-red-500 dark:border-red-900"
              >
                {t('appointments.drawer.declineAssignment')}
              </button>
              <button
                type="button"
                onClick={() => setSelectedId(appointment._id)}
                className="rounded-lg border border-zinc-300 px-3 py-2 text-sm dark:border-zinc-700"
              >
                {t('common.viewDetails')}
              </button>
            </div>
          )}
        </div>
      )}
    </article>
  );

  return (
    <>
      <div className="mb-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <div className="rounded-xl border border-zinc-200 bg-white p-4 dark:border-zinc-800 dark:bg-zinc-900">
          <p className="text-xs uppercase tracking-wide text-zinc-500">{t('staff.dashboard.needsAction')}</p>
          <p className="mt-1 text-2xl font-semibold">{summary?.pendingAssignments || 0}</p>
        </div>
        <div className="rounded-xl border border-zinc-200 bg-white p-4 dark:border-zinc-800 dark:bg-zinc-900">
          <p className="text-xs uppercase tracking-wide text-zinc-500">{t('staff.dashboard.confirmedThisWeek')}</p>
          <p className="mt-1 text-2xl font-semibold">{summary?.confirmedThisWeek || 0}</p>
        </div>
        <div className="rounded-xl border border-zinc-200 bg-white p-4 dark:border-zinc-800 dark:bg-zinc-900">
          <p className="text-xs uppercase tracking-wide text-zinc-500">{t('staff.dashboard.inProgress')}</p>
          <p className="mt-1 text-2xl font-semibold">{summary?.inProgressThisWeek || 0}</p>
        </div>
        <div className="rounded-xl border border-zinc-200 bg-white p-4 dark:border-zinc-800 dark:bg-zinc-900">
          <p className="text-xs uppercase tracking-wide text-zinc-500">{t('staff.dashboard.todayCount')}</p>
          <p className="mt-1 text-2xl font-semibold">{summary?.todayCount || 0}</p>
        </div>
      </div>

      {pendingAssignments.length > 0 && (
        <section className="mb-8 rounded-xl border border-amber-200 bg-amber-50/60 p-5 dark:border-amber-900/40 dark:bg-amber-950/20">
          <h3 className="font-semibold text-amber-900 dark:text-amber-200">{t('staff.dashboard.actionRequired')}</h3>
          <p className="mt-1 text-sm text-amber-800/80 dark:text-amber-200/70">{t('staff.dashboard.actionRequiredDesc')}</p>
          <div className="mt-4 space-y-3">
            {pendingAssignments.map((appointment) => renderAppointmentRow(appointment, true))}
          </div>
        </section>
      )}

      <section className="mb-8 rounded-xl border border-zinc-200 bg-white p-5 dark:border-zinc-800 dark:bg-zinc-900">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <h3 className="font-medium">{t('staff.dashboard.weekSchedule')}</h3>
            {weekRange?.start && weekRange?.end && (
              <p className="mt-1 text-sm text-zinc-500">{formatWeekRange(weekRange.start, weekRange.end)}</p>
            )}
          </div>
        </div>

        {groupedWeek.length === 0 ? (
          <p className="mt-4 text-sm text-zinc-600 dark:text-zinc-500">{t('staff.dashboard.noWeekAppointments')}</p>
        ) : (
          <div className="mt-6 space-y-6">
            {groupedWeek.map(([dayKey, rows]) => (
              <div key={dayKey}>
                <h4 className="mb-3 text-sm font-semibold text-glow-500">{formatDayLabel(dayKey, t)}</h4>
                <div className="space-y-3">
                  {rows.map((appointment) => renderAppointmentRow(appointment, false))}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      <AppointmentDetailDrawer
        appointmentId={selectedId}
        onClose={() => setSelectedId(null)}
      />
    </>
  );
};

export default StaffWeekSchedule;
